import { Injectable } from '@angular/core'
import { LogService } from 'htv-lib'
import { BehaviorSubject } from 'rxjs'
import { SubtitleData, VODLanguage, VODStatus, VODVideo } from '../interfaces/vod'
import { PlatformService } from '../platforms/platform.service'
import { ChannelPosition } from '../pages/channel/channel-position'
import { RouterPage, RouterService } from './router.service'
import { HttpClient } from '@angular/common/http'
import { StorageService } from './storage.service'
import { VODConfig } from '../interfaces/htv-config'
import { CurrentChannelData } from '../interfaces/channel-track-data'
import { LanguageService } from './language.service'

@Injectable({
  providedIn: 'root',
})
export class VODService {
  vods: VODVideo[] = []

  // Config properties
  enabled = false
  catalogUrl?: string

  // Selected VOD from catalog to VOD player
  selectedVOD?: VODVideo

  // Current VOD playing (only when on vod-player widget)
  currentVOD?: VODVideo

  readonly vodCatalogKey = 'VODCatalog'

  constructor(
    private httpClient: HttpClient,
    private languageService: LanguageService,
    private logService: LogService,
    private platformService: PlatformService,
    private routerService: RouterService,
    private storageService: StorageService
  ) {
    // do nothing
  }

  async init(config?: VODConfig): Promise<void> {
    this.logService.debug(`[VODService] init`)

    // init configuration values
    this.enabled = config?.enabled ?? false
    this.catalogUrl = config?.catalogUrl ?? undefined

    this.logService.info(`[VODService] enabled = ${this.enabled}`)
    this.logService.info(`[VODService] catalogUrl = ${this.catalogUrl}`)

    if (!this.enabled) {
      return
    }

    // load vod catalog from cache
    const localCatalog: VODVideo[] | undefined = this.storageService.getJson(this.vodCatalogKey)

    // load remote vod catalog
    let remoteCatalog: VODVideo[] | undefined
    if (this.catalogUrl) {
      try {
        remoteCatalog = await this.retrieveRemoteCatalog(this.catalogUrl)
      } catch (err: any) {
        remoteCatalog = undefined
      }
    }

    if (remoteCatalog) {
      // compare and save remote if there is changes
      try {
        if (JSON.stringify(localCatalog) != JSON.stringify(remoteCatalog)) {
          this.logService.debug(`[VODService] Saving VOD catalog`)
          this.storageService.saveJson(this.vodCatalogKey, remoteCatalog)
        }
      } catch (err: any) {
        this.logService.debug(`[VODService] Could not update local catalog, error = ${err?.message}`)
      }

      this.load(remoteCatalog)
    } else {
      if (localCatalog) {
        this.load(localCatalog)
      } else {
        this.logService.debug(`[VODService] Could not find local VOD catalog`)
      }
    }
  }

  retrieveRemoteCatalog(catalogUrl: string): Promise<VODVideo[]> {
    return new Promise((resolve, reject) => {
      this.httpClient.get<VODVideo[]>(catalogUrl).subscribe({
        next: (res: VODVideo[]) => {
          this.logService.info(`[VODService] Successfully retrieved remote VOD catalog`)
          resolve(res)
        },
        error: (err: any) => {
          this.logService.error(`[VODService] Could not retrieve remote VOD catalog, error = ${err?.message}`)
          reject()
        },
      })
    })
  }

  // Function with data from Anantha but still waiting for the "final" version
  /*
  retrieveRemoteCatalog(catalogUrl: string): Promise<VODVideo[]> {
    return new Promise((resolve, reject) => {
      this.httpClient.get<CatalogVODDto[]>(`${catalogUrl}/vod.json`).subscribe({
        next: (res: CatalogVODDto[]) => {
          this.logService.info(`[VODService] Successfully retrieved remote VOD catalog`)

          resolve(
            res.map(i => {
              const id = i['@attributes'].id

              const langs: VODLanguage[] = i.languages.language.map(language => ({
                code: language['@attributes'].id,
                title: language.title,
                short: language.short,
                full: typeof language.full === 'string' ? language.full : undefined,
                credits: {
                  directors: language.credits.directors.director,
                  actors: language.credits.actors.actor,
                },
              }))

              const metadata: VODMetadata = {
                certificate: i?.certificate,
                creator: i['@attributes'].creator,
                duration: Number(i?.duration),
                genres: i?.genres.genre,
                type: i['@attributes'].type,
                year: i?.year,
              }

              let videoUrl
              let trailerUrl
              i.assets.asset.forEach((asset: any) => {
                switch (asset['@attributes'].class) {
                  case 'film': {
                    // TODO: checking url format for tests because i have both
                    //       change when we know the final format
                    const url = asset['@attributes'].name
                    if (url.startsWith('http://') || url.startsWith('https://')) {
                      videoUrl = url
                    } else {
                      videoUrl = `${catalogUrl}/${asset['@attributes'].name}`
                    }
                    break
                  }
                  case 'trailer':
                    trailerUrl = `${catalogUrl}/${asset['@attributes'].name}`
                    break
                }
              })

              let bannerUrl
              let thumbnailUrl
              i.images.image.forEach((image: string) => {
                if (image.endsWith('-hero.jpg')) {
                  bannerUrl = `${catalogUrl}/${image}`
                }
                if (image.endsWith('-standard.jpg')) {
                  thumbnailUrl = `${catalogUrl}/${image}`
                }
              })

              // TODO: Probably need to change this when we know how to retrieve DRM stuff
              const drm = i['@attributes'].drm ?? false // TODO: change when we known the final format
              let drmConfig
              const widevineLicenseUrl = i['@attributes'].drmWidevineLicenseUrl ?? undefined
              if (widevineLicenseUrl && widevineLicenseUrl.length > 0) {
                drmConfig = {
                  type: VODDRMType.WIDEVINE,
                  widevineLicenseUrl: widevineLicenseUrl,
                }
              }

              return {
                id: id,
                langs: langs,
                metadata: metadata,
                videoUrl: videoUrl,
                trailerUrl: trailerUrl,
                bannerUrl: bannerUrl,
                thumbnailUrl: thumbnailUrl,
                drm: drm,
                drmConfig: drmConfig,
              }
            })
          )
        },
        error: (err: any) => {
          this.logService.error(`[VODService] Could not retrieve remote VOD catalog, error = ${err?.message}`)
          reject()
        },
      })
    })
  }
  */

  load(vods: VODVideo[]): void {
    if (vods) {
      this.vods = vods.map(vod => ({
        ...vod,
        state: {
          status$: new BehaviorSubject<VODStatus>(VODStatus.NONE),
          duration$: new BehaviorSubject<number | undefined>(undefined),
          currentTime$: new BehaviorSubject<number | undefined>(undefined),
          tracks$: new BehaviorSubject<CurrentChannelData | undefined>(undefined),
          subtitleEnabled$: new BehaviorSubject<boolean>(false),
          subtitleChange$: new BehaviorSubject<SubtitleData | undefined>(undefined),
        },
      }))
    } else {
      this.logService.error(`[VODService] Could not load vods, invalid vods array`)
    }
  }

  getVOD(id: string): VODVideo | undefined {
    const index = this.vods.findIndex(v => v.id === id)
    if (index > -1) {
      return this.vods[index]
    } else {
      return undefined
    }
  }

  getVODLang(vod?: VODVideo, lang?: string): VODLanguage | undefined {
    if (vod === undefined || vod.langs === undefined) {
      return undefined
    }
    const code = lang ?? this.languageService.getSelectedLanguage().code
    const index = vod.langs.findIndex(l => l.code === code)
    if (index > -1) {
      return vod.langs[index]
    } else {
      // english if not found
      return this.getVODLang(vod, 'en')
    }
  }

  openVOD(vod: VODVideo): void {
    this.selectedVOD = vod
    if (!this.routerService.isCurrentPage(RouterPage.VOD)) {
      this.routerService.goToPage(RouterPage.VOD)
    }
  }

  initVOD(id: string): void {
    const vod = this.getVOD(id)
    if (!vod) {
      this.logService.info(`[VODService] Could not find vod with id: ${id}`)
      return
    }

    this.resetVOD()
    this.currentVOD = vod
  }

  resetVOD(): void {
    if (this.currentVOD) {
      this.currentVOD = undefined
    }
  }

  startVOD(id: string, pos: ChannelPosition): void {
    if (this.currentVOD) {
      this.logService.info(`[VODService] Starting VOD ${this.currentVOD.id}`)
      this.platformService.vodStart(this.currentVOD, pos)
    }
  }

  stopVOD(): void {
    if (this.currentVOD) {
      this.platformService.vodStop()
    }
    this.resetVOD()
  }

  play(): void {
    if (this.currentVOD) {
      this.platformService.vodPlay(this.currentVOD)
    }
  }

  pause(): void {
    if (this.currentVOD) {
      this.platformService.vodPause(this.currentVOD)
    }
  }

  setSpeed(speed: number): void {
    if (this.currentVOD) {
      this.platformService.vodSpeed(this.currentVOD, speed)
    }
  }

  seekTo(milliseconds: number): Promise<void> {
    if (this.currentVOD) {
      return this.platformService.vodSeekTo(this.currentVOD, milliseconds)
    } else {
      return Promise.reject()
    }
  }

  jumpBackward(milliseconds: number): Promise<void> {
    if (this.currentVOD) {
      return this.platformService.vodJumpBackward(this.currentVOD, milliseconds)
    } else {
      return Promise.reject()
    }
  }

  jumpForward(milliseconds: number): Promise<void> {
    if (this.currentVOD) {
      return this.platformService.vodJumpForward(this.currentVOD, milliseconds)
    } else {
      return Promise.reject()
    }
  }
}
